import { useContext, useState } from "react";
import CuratedSearchDesignImproveResultsModal, {
  CuratedSearchDesignImproveResultsModal_searchAnalysisPreview,
} from "./CuratedSearchDesignImproveResultsModal";
import classNames from "classnames";
import CiroButton, { CiroButtonStyleEnum } from "../../shared/CiroButton";
import AutopilotPreviewRunAutopilotModal from "../../autopilotPreview/AutopilotPreviewRunAutopilotModal";
import AutopilotSessionContinueSearchModal from "../../automation/autopilotSession/AutopilotSessionContinueSearchModal";
import { gql, useQuery } from "@apollo/client";

import {
  AutopilotSessionType,
  CuratedSearchDesignPostAnalysisActions_AutopilotSessionsQuery,
  CuratedSearchDesignPostAnalysisActions_AutopilotSessionsQueryVariables,
  OpensearchCuratedSearch,
} from "../../../__generated__/graphql";
import CiroSpinner from "../../shared/CiroSpinner";
import { useNavigate } from "react-router-dom";
import InternalAutopilotHydrationContext, {
  PreviewContactFeedbackMap,
} from "../../../contexts/InternalAutopilotHydrationContext";

const CuratedSearchDesignPostAnalysisActions_AutopilotSessions = gql`
  query CuratedSearchDesignPostAnalysisActions_AutopilotSessions(
    $input: AutopilotSessionsInput!
  ) {
    autopilotSessions(input: $input) {
      id
      name
      number_contacts_scanned
      curatedSearch {
        internalCuratedSearch {
          total_profiles
        }
      }
    }
  }
`;

export const CuratedSearchDesignPostAnalysisActions_SearchAnalysisPreview = gql`
  fragment CuratedSearchDesignPostAnalysisActions_SearchAnalysisPreview on SearchAnalysisPreviewResults {
    ...CuratedSearchDesignImproveResultsModal_searchAnalysisPreview
  }

  ${CuratedSearchDesignImproveResultsModal_searchAnalysisPreview}
`;

const CuratedSearchDesignPostAnalysisActions = ({
  totalContacts,
  filters,
  curatedSearchId,
  onRemix,
  previewContactFeedback,
  filtersAreOpen,
}: {
  totalContacts: number;
  filters: OpensearchCuratedSearch;
  curatedSearchId: number;
  onRemix: (prompt: string) => void;
  previewContactFeedback: PreviewContactFeedbackMap;
  filtersAreOpen: boolean;
}) => {
  const navigate = useNavigate();
  const { resetHydrationValues } = useContext(
    InternalAutopilotHydrationContext,
  );
  const [isContinueSearchModalOpen, setIsContinueSearchModalOpen] =
    useState(false);
  const [isRunAutopilotModalOpen, setIsRunAutopilotModalOpen] = useState(false);
  const [showImproveResultsModal, setShowImproveResultsModal] = useState(false);

  const {
    data: existingAutopilotSessionsData,
    loading: existingAutopilotSessionsLoading,
  } = useQuery<
    CuratedSearchDesignPostAnalysisActions_AutopilotSessionsQuery,
    CuratedSearchDesignPostAnalysisActions_AutopilotSessionsQueryVariables
  >(CuratedSearchDesignPostAnalysisActions_AutopilotSessions, {
    variables: {
      input: {
        searchType: AutopilotSessionType.Internal,
        internalSearchFilters: {
          opensearchFiltersBlob: filters.opensearchFiltersBlob ?? "",
          auxiliary_filters: {
            companyNames: filters.auxiliary_filters?.companyNames ?? [],
            orgCompanyIds: filters.auxiliary_filters?.orgCompanyIds ?? [],
          },
        },
      },
    },
  });

  const existingAutopilotSession =
    existingAutopilotSessionsData?.autopilotSessions[0] ?? null;

  const hasFeedback = previewContactFeedback.size > 0;

  return (
    <div className={classNames("flex", "justify-end", "gap-2")}>
      <CiroButton
        analyticsField="Open post analysis remix section"
        onClick={() => setShowImproveResultsModal(true)}
        style={CiroButtonStyleEnum.SECONDARY}
      >
        {hasFeedback ? "Improve results with feedback" : "Improve results"}
      </CiroButton>
      <CiroButton
        disabled={
          totalContacts === 0 ||
          existingAutopilotSessionsLoading ||
          filtersAreOpen
        }
        tooltip={
          filtersAreOpen
            ? "Filters are still being edited, please close them to start autopilot"
            : undefined
        }
        analyticsField="Start autopilot internal search"
        style={CiroButtonStyleEnum.LOUD}
        onClick={() =>
          existingAutopilotSessionsData?.autopilotSessions.length
            ? setIsContinueSearchModalOpen(true)
            : setIsRunAutopilotModalOpen(true)
        }
      >
        <div className={classNames("flex", "items-center", "gap-1")}>
          {existingAutopilotSessionsLoading ? (
            <CiroSpinner loading />
          ) : (
            <span>
              {hasFeedback
                ? "Continue without feedback"
                : "Search for more contacts"}
            </span>
          )}
        </div>
      </CiroButton>
      <AutopilotPreviewRunAutopilotModal
        isOpen={isRunAutopilotModalOpen}
        onClose={() => setIsRunAutopilotModalOpen(false)}
        curatedSearchId={curatedSearchId}
        totalContacts={totalContacts}
        isMultisearch={false}
        onStartedAutopilot={() => {}}
      />
      <CuratedSearchDesignImproveResultsModal
        isOpen={showImproveResultsModal}
        onClose={() => setShowImproveResultsModal(false)}
        suggestedPrompts={[]}
        handleSubmitRemix={onRemix}
      />
      {existingAutopilotSession && (
        <AutopilotSessionContinueSearchModal
          isOpen={isContinueSearchModalOpen}
          onClose={() => setIsContinueSearchModalOpen(false)}
          autopilotSessionId={existingAutopilotSession.id}
          onRestartedSession={() => {
            navigate(`/autopilot/session/${existingAutopilotSession.id}`);
            resetHydrationValues({});
          }}
          runningState={null}
          contactsScanned={existingAutopilotSession.number_contacts_scanned}
          totalProfiles={
            existingAutopilotSession.curatedSearch?.internalCuratedSearch
              ?.total_profiles
          }
          customName={`Existing search found. Continue searching ${existingAutopilotSession.name}`}
          initialSkipContactsYouveAlreadySeen={false}
        />
      )}
    </div>
  );
};

export default CuratedSearchDesignPostAnalysisActions;
